import {
  AfterViewInit,
  Component,
  Inject,
  AfterViewChecked,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { NumeralPipe } from '@pipes/numeral.pipe';
import {
  CreateQTSFormGroup,
  DeviceTypeValidatorViewModel,
} from '@services/qts-formgroup';
import { differenceInDays } from 'date-fns';
import {
  DeviceSettingBase,
  resetSensorSettings,
  sensorsHavingForm,
} from '@utils/device-setting.util';
import { MinutesLabelPipe } from '@pipes/minutes-label.pipe';

/** ダイアログのViewModel */
export type DialogOption = {
  updating: boolean;
  deviceName: string;
  serialNo: string;
  status: string;
  lastUpdatedAt: Date | null;
  plannedUpdatedAt: Date | null;
  battery: number | null;
  assettypeList?: AssettypeViewModel[];
  deviceType: DeviceTypeViewModel;
  target?: DeviceSettingViewModel | null;
  isSettingGroupName: boolean;
  contractExpiredAt: string;
  deviceNameEditDisabled?: boolean;
  isFromShipment?: boolean;
  shipmentTrackingEditable?: boolean;
};

/** アセットタイプ */
export type AssettypeViewModel = DeviceSettingBase & {
  /** アセットタイプID */
  assettypeId: string;
  /** アセットタイプ名 */
  assettypeName: string;
  /** テナントID */
  tenantId: string;
  /** デバイスステータス */
  deviceActiveStatus: boolean;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** センサーデータ収集間隔 */
  samplingFrequency: number;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number;
  /** フライトモード */
  fmd?: boolean;
  /** 作成者 */
  createdBy: string;
  /** 更新者 */
  updatedBy: string;
  /** 作成日時 */
  createdAt: Date;
  /** 更新日時 */
  updatedAt: Date;
};
/** デバイス設定のViewModel */
export type DeviceSettingViewModel = DeviceSettingBase & {
  /** デバイスID */
  deviceId?: string;
  /** デバイス名 */
  deviceName?: string;
  /** デバイスグループID */
  deviceSettingGroupId?: string;
  /** デバイス種類ID */
  deviceTypeId?: string;
  /** センサーデータ収集間隔 */
  samplingFrequency?: number | null;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number | null;
  /** フライトモード */
  fmd?: boolean;
};

/** デバイス種類のViewModel */
export type DeviceTypeViewModel = {
  /** デバイス種類ID */
  deviceTypeId: string;
  /** 説明文 */
  description?: string;
  /** センサーデータ収集間隔 */
  samplingFrequency?: number[];
  /** クラウドデータ送信間隔 */
  reportingFrequency?: number[];
  /** 温度センサー */
  temperatureSensor?: boolean;
  /** 気圧センサー */
  pressureSensor?: boolean;
  /** 湿度センサー */
  humiditySensor?: boolean;
  /** 傾斜センサー */
  tiltSensor?: boolean;
  /** 照度センサー */
  lightSensor?: boolean;
  /** 位置測位 */
  locationSensor?: boolean;
  /** 位置測位_GNSS */
  locationTechGnss?: boolean;
  /** 位置測位_Wi-Fi */
  locationTechWifi?: boolean;
  /** 位置測位_Cellular */
  locationTechCellular?: boolean;
  /** 落下イベント検知 */
  fallDetected?: boolean;
  /** 動作イベント検知 */
  mcdDetected?: boolean;
  /** 明暗イベント検知 */
  lightExposureDetected?: boolean;
  /** 衝撃イベント検知 */
  shockDetected?: boolean;
  /** フライトモード */
  fmd?: boolean;
};

/** フォームグループ */
type DeviceSettingFormGroup = FormGroup<{
  /** デバイス名 */
  deviceName: FormControl<string>;
  /** センサーデータ収集期間 */
  samplingFrequency: FormControl<number>;
  /** クラウドデータ収集期間 */
  reportingFrequency: FormControl<number | null>;
  /** 位置検知 */
  locationDetected: FormControl<boolean>;
  /** 位置測位技術優先度 */
  locationPriority: FormGroup<{
    locationPriority1: FormControl<string>;
    locationPriority2: FormControl<string>;
    locationPriority3: FormControl<string>;
  }>;
  /** 落下検知 */
  fallDetected: FormControl<boolean>;
  /** 動作検知 */
  mcdDetected: FormControl<boolean>;
  /** 明暗検知 */
  lightExposureDetected: FormControl<boolean>;
  /** 明暗検知設定値 */
  lightExposureDetectValue: FormControl<string>;
  /** 衝撃検知 */
  shockDetected: FormControl<boolean>;
  /** 衝撃検知設定値 */
  shockDetectedDetectValue: FormControl<number>;
  /** 温度センサー */
  temperature: FormControl<boolean>;
  /** 温度センサー設定 */
  temperatureSettings: FormGroup<{
    /** 温度センサー：上限逸脱 */
    temperatureAlertMax: FormControl<number>;
    /** 温度センサー：上限警告 */
    temperaturePrealertMax: FormControl<number>;
    /** 温度センサー：下限警告 */
    temperaturePrealertMin: FormControl<number>;
    /** 温度センサー：下限逸脱 */
    temperatureAlertMin: FormControl<number>;
  }>;
  /** 気圧センサー */
  pressure: FormControl<boolean>;
  /** 気圧センサー設定 */
  pressureSettings: FormGroup<{
    /** 気圧センサー：上限逸脱 */
    pressureAlertMax: FormControl<number>;
    /** 気圧センサー：上限警告 */
    pressurePrealertMax: FormControl<number>;
    /** 気圧センサー：下限警告 */
    pressurePrealertMin: FormControl<number>;
    /** 気圧センサー：下限逸脱 */
    pressureAlertMin: FormControl<number>;
  }>;
  /** 湿度センサー */
  humidity: FormControl<boolean>;
  /** 湿度センサー設定 */
  humiditySettings: FormGroup<{
    /** 湿度センサー：上限逸脱 */
    humidityAlertMax: FormControl<number>;
    /** 湿度センサー：上限警告 */
    humidityPrealertMax: FormControl<number>;
    /** 湿度センサー：下限警告 */
    humidityPrealertMin: FormControl<number>;
    /** 湿度センサー：下限逸脱 */
    humidityAlertMin: FormControl<number>;
  }>;
  /** 傾斜センサー */
  tilt: FormControl<boolean>;
  /** 傾斜センサー設定 */
  tiltSettings: FormGroup<{
    /** 傾斜センサー：上限逸脱 */
    tiltAlertMax: FormControl<number>;
    /** 傾斜センサー：上限警告 */
    tiltPrealertMax: FormControl<number>;
    /** 傾斜センサー：下限警告 */
    tiltPrealertMin: FormControl<number>;
    /** 傾斜センサー：下限逸脱 */
    tiltAlertMin: FormControl<number>;
  }>;
  /** 照度センサー */
  light: FormControl<boolean>;
  /** 照度センサー設定 */
  lightSettings: FormGroup<{
    /** 照度センサー：上限逸脱 */
    lightAlertMax: FormControl<number>;
    /** 照度センサー：上限警告 */
    lightPrealertMax: FormControl<number>;
    /** 照度センサー：下限警告 */
    lightPrealertMin: FormControl<number>;
    /** 照度センサー：下限逸脱 */
    lightAlertMin: FormControl<number>;
  }>;

  /** 配送機能での利用許可 */
  shipmentTracking: FormControl<boolean>;
}>;

@Component({
  selector: 'app-asset-type-edit-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatFormFieldModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatIcon,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    NumeralPipe,
    MinutesLabelPipe,
  ],
  templateUrl: './device-setting-edit-dialog.component.html',
  styleUrl: './device-setting-edit-dialog.component.scss',
})
export class DeviceSettingEditDialogComponent
  implements AfterViewInit, AfterViewChecked, OnInit, OnDestroy
{
  /** 閲覧可能 */
  public readonly: boolean | undefined;

  /** 更新中 */
  public updating: boolean;

  /** デバイス名 */
  public deviceName: string;

  /** シリアル番号 */
  public serialNo: string;

  /** デバイスステータス */
  public status: string;

  /** 最終更新日 */
  public lastUpdatedAt: Date | null;

  /** 次回更新予定日 */
  public plannedUpdatedAt: Date | null;

  /** バッテリー */
  public battery: number | null;

  /** デバイス設定詳細初期表示値 */
  public initDeviceGroupSetting: DeviceSettingViewModel;

  /** デバイス設定詳細初期表示値（DeviceSettingBaseの型に合わせたもの） */
  public initDeviceSetting: DeviceSettingBase;

  /** アセットタイプ設定時、トグルOFF時の変更先の設定値 */
  public assettypeRestoreValue: DeviceSettingBase;

  /** アセットタイプ一覧 */
  public assettypeList: AssettypeViewModel[];

  /** デバイス種類 */
  public deviceType: DeviceTypeViewModel;

  /** バリデーション用デバイス種類 */
  public validatorDeviceType: DeviceTypeValidatorViewModel;

  /** フォームグループ */
  public formGroup: FormGroup;

  /** 測定間隔リスト */
  public samplingFrequencies: number[] = [];

  /** 送信間隔リスト */
  public reportingFrequencies: number[] = [];

  /** フィルター済み送信間隔リスト */
  public filteredReportingFrequencies: number[] = [];

  /** 位置測位技術優先度のリスト */
  public locationPriorities: { [key: string]: string } =
    this.createFormGroup.locationPriorities;

  /** 位置測位技術優先度のドロップダウン表示数 */
  public locationPrioritiesNum: number;

  /** 明暗検知設定値のリスト */
  public lightExposureDetectValues: { [key: string]: string } =
    this.createFormGroup.defaultLightExposureDetectValues;

  /** 衝撃検知設定値のリスト */
  public shockDetectedDetectValues: number[] =
    this.createFormGroup.defaultShockDetectedDetectValues;

  /** 設定グループに所属しているか */
  public isSettingGroupName: boolean = false;

  /** デバイス契約終了日 */
  private contractExpiredAt: string;

  /** 期間終了フラグ */
  public isExpired: boolean = false;

  /** 配送設定から開かれているか */
  public isFromShipment: boolean = false;

  /** コンポーネント破棄時にObservableを解放するためのSubject */
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) options: DialogOption,
    private dialogRef: MatDialogRef<DeviceSettingFormGroup>,
    private dialog: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private createFormGroup: CreateQTSFormGroup,
  ) {
    this.updating = options.updating;
    this.deviceName = options.deviceName;
    this.serialNo = options.serialNo;
    this.status = options.status;
    this.lastUpdatedAt = options.lastUpdatedAt;
    this.plannedUpdatedAt = options.plannedUpdatedAt;
    this.battery = options.battery;
    this.assettypeList = options.assettypeList || [];
    this.deviceType = options.deviceType;
    this.validatorDeviceType = options.deviceType;
    this.samplingFrequencies =
      options.deviceType.samplingFrequency || this.samplingFrequencies;
    this.reportingFrequencies =
      options.deviceType.reportingFrequency || this.reportingFrequencies;
    this.isSettingGroupName = options.isSettingGroupName;
    this.contractExpiredAt = options.contractExpiredAt;
    this.isFromShipment = options.isFromShipment ?? false;

    this.initDeviceGroupSetting = {
      deviceName: options.deviceName,
      deviceSettingGroupId: options.target?.deviceSettingGroupId,
      deviceTypeId: options.target?.deviceTypeId,
      samplingFrequency: options.target?.samplingFrequency || null,
      reportingFrequency: options.target?.reportingFrequency || null,
      shipmentTracking: options.target?.shipmentTracking || false,
      locationDetected: options.target?.locationDetected || false,
      locationPriority: options.target?.locationPriority,
      fallDetected: options.target?.fallDetected || false,
      mcdDetected: options.target?.mcdDetected || false,
      lightExposureDetected: options.target?.lightExposureDetected || false,
      lightExposureDetectValue: options.target?.lightExposureDetectValue || '',
      shockDetected: options.target?.shockDetected || false,
      shockDetectedDetectValue: options.target?.shockDetectedDetectValue || 0,
      temperature: options.target?.temperature || false,
      temperatureSettings: options.target?.temperatureSettings,
      pressure: options.target?.pressure || false,
      pressureSettings: options.target?.pressureSettings,
      humidity: options.target?.humidity || false,
      humiditySettings: options.target?.humiditySettings,
      tilt: options.target?.tilt || false,
      tiltSettings: options.target?.tiltSettings,
      light: options.target?.light || false,
      lightSettings: options.target?.lightSettings,
    };
    this.initDeviceSetting = {
      shipmentTracking: this.initDeviceGroupSetting.shipmentTracking,
      locationDetected: this.initDeviceGroupSetting.locationDetected || false,
      locationPriority: this.initDeviceGroupSetting.locationPriority,
      fallDetected: this.initDeviceGroupSetting.fallDetected || false,
      mcdDetected: this.initDeviceGroupSetting.mcdDetected || false,
      lightExposureDetected:
        this.initDeviceGroupSetting.lightExposureDetected || false,
      lightExposureDetectValue:
        this.initDeviceGroupSetting.lightExposureDetectValue || '',
      shockDetected: this.initDeviceGroupSetting.shockDetected || false,
      shockDetectedDetectValue:
        this.initDeviceGroupSetting.shockDetectedDetectValue || 0,
      temperature: this.initDeviceGroupSetting.temperature || false,
      temperatureSettings: this.initDeviceGroupSetting.temperatureSettings,
      pressure: this.initDeviceGroupSetting.pressure || false,
      pressureSettings: this.initDeviceGroupSetting.pressureSettings,
      humidity: this.initDeviceGroupSetting.humidity || false,
      humiditySettings: this.initDeviceGroupSetting.humiditySettings,
      tilt: this.initDeviceGroupSetting.tilt || false,
      tiltSettings: this.initDeviceGroupSetting.tiltSettings,
      light: this.initDeviceGroupSetting.light || false,
      lightSettings: this.initDeviceGroupSetting.lightSettings,
    };

    this.assettypeRestoreValue = this.initDeviceSetting;

    const emptyAssettype: AssettypeViewModel = {
      assettypeId: '',
      assettypeName: '',
      tenantId: '',
      deviceActiveStatus: false,
      deviceTypeId: '',
      samplingFrequency: 0,
      reportingFrequency: 0,
      shipmentTracking: false,
      locationDetected: false,
      locationPriority: [],
      fallDetected: false,
      mcdDetected: false,
      lightExposureDetected: false,
      lightExposureDetectValue: '',
      shockDetected: false,
      shockDetectedDetectValue: 0,
      temperature: false,
      temperatureSettings: [],
      pressure: false,
      pressureSettings: [],
      humidity: false,
      humiditySettings: [],
      tilt: false,
      tiltSettings: [],
      light: false,
      lightSettings: [],
      createdBy: '',
      updatedBy: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    this.assettypeList.unshift(emptyAssettype);

    this.locationPriorities = this.createFormGroup.updateLocationPriorities(
      this.deviceType?.locationTechCellular,
      this.deviceType?.locationTechGnss,
      this.deviceType?.locationTechWifi,
    );

    this.locationPrioritiesNum = Object.keys(this.locationPriorities).length;

    this.formGroup = this.createFormGroup.createQTSFormGroup(
      options.target!,
      this.validatorDeviceType,
    );

    // [#159]端末側の不具合のため、動作は設定不可とする
    this.formGroup.controls['mcdDetected'].setValue(false, {
      emitEvent: false,
    });
    this.formGroup.controls['mcdDetected'].disable();

    this.formGroup.addControl(
      'deviceName',
      new FormControl<string>(options.target?.deviceName || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    );

    this.formGroup.addControl(
      'shipmentTracking',
      new FormControl<boolean>(options.target?.shipmentTracking || false, {
        nonNullable: true,
      }),
    );

    if (this.isSettingGroupName) {
      this.formGroup.disable();
    } else if (!options.shipmentTrackingEditable) {
      this.formGroup.get('shipmentTracking')?.disable();
    }

    const result = differenceInDays(
      new Date(),
      new Date(this.contractExpiredAt),
    );

    if (result > 0) {
      this.formGroup.disable();
      this.isExpired = true;
    }
  }

  ngOnInit(): void {
    // 各センサーのトグル監視をまとめて登録
    sensorsHavingForm.forEach((sensor) => this.watchSensorToggle(sensor));
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngAfterViewInit(): void {
    this.createFormGroup.subscribeFormChanges(this.formGroup);
  }

  /**
   * 送信
   */
  public async submit() {
    const deviceName =
      this.formGroup.get('deviceName')?.value || this.deviceName;
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `デバイス「${deviceName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    let deviceSetting: DeviceSettingViewModel;
    if (this.isSettingGroupName) {
      deviceSetting = this.initDeviceGroupSetting;
      deviceSetting.deviceName = this.formGroup.get('deviceName')?.value || '';
    } else {
      deviceSetting = {
        deviceName: this.formGroup.get('deviceName')?.value || '',
        reportingFrequency:
          this.formGroup.get('reportingFrequency')?.value || 0,
        shipmentTracking:
          this.formGroup.get('shipmentTracking')?.value || false,
      };

      if (this.formGroup.get('samplingFrequency')?.value) {
        deviceSetting.samplingFrequency =
          this.formGroup.get('samplingFrequency')?.value;
      }

      // デバイス種類で有効化されている項目のみリクエストに追加する
      if (this.deviceType.locationSensor) {
        deviceSetting.locationDetected =
          this.formGroup.get('locationDetected')?.value || false;
        deviceSetting.locationPriority = [
          this.formGroup.get('locationPriority')?.get('locationPriority1')
            ?.value || '',
          this.formGroup.get('locationPriority')?.get('locationPriority2')
            ?.value || '',
          this.formGroup.get('locationPriority')?.get('locationPriority3')
            ?.value || '',
        ];
      }
      if (this.deviceType.fallDetected) {
        deviceSetting.fallDetected =
          this.formGroup.get('fallDetected')?.value || false;
      }
      if (this.deviceType.mcdDetected) {
        deviceSetting.mcdDetected =
          this.formGroup.get('mcdDetected')?.value || false;
      }
      if (this.deviceType.lightExposureDetected) {
        deviceSetting.lightExposureDetected =
          this.formGroup.get('lightExposureDetected')?.value || false;
        deviceSetting.lightExposureDetectValue =
          this.formGroup.get('lightExposureDetectValue')?.value || 'continuous';
      }
      if (this.deviceType.shockDetected) {
        deviceSetting.shockDetected =
          this.formGroup.get('shockDetected')?.value || false;
        deviceSetting.shockDetectedDetectValue =
          this.formGroup.get('shockDetectedDetectValue')?.value || 0;
      }
      if (this.deviceType.temperatureSensor) {
        deviceSetting.temperature =
          this.formGroup.get('temperature')?.value || false;
        deviceSetting.temperatureSettings = [
          this.formGroup.get('temperatureSettings')?.get('temperatureAlertMax')
            ?.value || 0,
          this.formGroup.get('temperatureSettings')?.get('temperatureAlertMin')
            ?.value || 0,
          this.formGroup
            .get('temperatureSettings')
            ?.get('temperaturePrealertMax')?.value || 0,
          this.formGroup
            .get('temperatureSettings')
            ?.get('temperaturePrealertMin')?.value || 0,
        ];
      }
      if (this.deviceType.pressureSensor) {
        deviceSetting.pressure = this.formGroup.get('pressure')?.value || false;
        deviceSetting.pressureSettings = [
          this.formGroup.get('pressureSettings')?.get('pressureAlertMax')
            ?.value || 0,
          this.formGroup.get('pressureSettings')?.get('pressureAlertMin')
            ?.value || 0,
          this.formGroup.get('pressureSettings')?.get('pressurePrealertMax')
            ?.value || 0,
          this.formGroup.get('pressureSettings')?.get('pressurePrealertMin')
            ?.value || 0,
        ];
      }
      if (this.deviceType.humiditySensor) {
        deviceSetting.humidity = this.formGroup.get('humidity')?.value || false;
        deviceSetting.humiditySettings = [
          this.formGroup.get('humiditySettings')?.get('humidityAlertMax')
            ?.value || 0,
          this.formGroup.get('humiditySettings')?.get('humidityAlertMin')
            ?.value || 0,
          this.formGroup.get('humiditySettings')?.get('humidityPrealertMax')
            ?.value || 0,
          this.formGroup.get('humiditySettings')?.get('humidityPrealertMin')
            ?.value || 0,
        ];
      }
      if (this.deviceType.tiltSensor) {
        deviceSetting.tilt = this.formGroup.get('tilt')?.value || false;
        deviceSetting.tiltSettings = [
          this.formGroup.get('tiltSettings')?.get('tiltAlertMax')?.value || 0,
        ];
      }
      if (this.deviceType.lightSensor) {
        deviceSetting.light = this.formGroup.get('light')?.value || false;
        deviceSetting.lightSettings = [
          this.formGroup.get('lightSettings')?.get('lightAlertMax')?.value || 0,
          this.formGroup.get('lightSettings')?.get('lightAlertMin')?.value || 0,
          this.formGroup.get('lightSettings')?.get('lightPrealertMax')?.value ||
            0,
          this.formGroup.get('lightSettings')?.get('lightPrealertMin')?.value ||
            0,
        ];
      }
      if (this.deviceType.fmd) {
        deviceSetting.fmd = this.formGroup.get('fmd')?.value || false;
      }
    }
    this.dialogRef.close(deviceSetting);
  }

  public selectAsset(assettypeId: string) {
    if (assettypeId) {
      const targetAssettype = this.assettypeList.filter(
        (asset) => asset['assettypeId'] == assettypeId,
      )[0];
      this.createForm(targetAssettype);
      // // トグルOff時の変更先の値を設定テンプレートものにしたいため、初期値を設定テンプレートの値に更新。
      this.assettypeRestoreValue = targetAssettype;
    } else {
      this.createForm(this.initDeviceGroupSetting);
      this.assettypeRestoreValue = this.initDeviceSetting;
    }
    this.createFormGroup.subscribeFormChanges(this.formGroup);
    // 各センサーのトグル監視をまとめて登録（フォームが作り変えられるため再度実行）
    sensorsHavingForm.forEach((sensor) => this.watchSensorToggle(sensor));
  }

  private createForm(target: AssettypeViewModel | DeviceSettingViewModel) {
    this.locationPriorities = this.createFormGroup.updateLocationPriorities(
      this.deviceType?.locationTechCellular,
      this.deviceType?.locationTechGnss,
      this.deviceType?.locationTechWifi,
    );

    this.locationPrioritiesNum = Object.keys(this.locationPriorities).length;

    this.formGroup = this.createFormGroup.createQTSFormGroup(
      target,
      this.validatorDeviceType,
    );

    const deviceName = this.deviceName || this.serialNo;

    this.formGroup.addControl(
      'deviceName',
      new FormControl<string>(deviceName || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    );

    this.formGroup.addControl(
      'shipmentTracking',
      new FormControl<boolean>(target?.shipmentTracking || false, {
        nonNullable: true,
      }),
    );

    // [#159]端末側の不具合のため、動作は設定不可とする
    this.formGroup.controls['mcdDetected'].setValue(false, {
      emitEvent: false,
    });
    this.formGroup.controls['mcdDetected'].disable();

    const result = differenceInDays(
      new Date(),
      new Date(this.contractExpiredAt),
    );

    if (result > 0) {
      for (const formControlName in this.formGroup.controls) {
        this.formGroup.controls[formControlName].disable();
      }
    }
  }

  /**
   * 送信間隔の選択肢が選択可能（非活性）か
   *
   * @param reportingFrequency 送信間隔の選択肢
   * @returns trueなら選択可能
   */
  public getDisabledReportingFrequency(reportingFrequency: number) {
    const samplingFrequency =
      this.formGroup.controls['samplingFrequency'].value;

    return this.createFormGroup.isDisabledReportingFrequency(
      samplingFrequency,
      reportingFrequency,
    );
  }

  public getLocationPriorityDisabled(
    value: string,
    compareValue1: string | null,
    compareValue2: string | null,
  ) {
    return this.createFormGroup.isLocationPriorityDisabled(
      value,
      compareValue1,
      compareValue2,
    );
  }

  /**
   * 送信間隔の値が送信間隔のリストに含まれているか判定する。
   */
  public reportingFrequencyValueChanged(): void {
    if (
      !this.reportingFrequencies.includes(
        this.formGroup.get('reportingFrequency')?.value,
      )
    ) {
      this.formGroup.controls['reportingFrequency'].setValue(null);
    }
  }

  /**
   * トグルの変更を監視して、トグルがOFFになったときに入力フォームを初期値に戻す
   */
  private watchSensorToggle(sensor: string): void {
    const control = this.formGroup.get(sensor);
    if (!control) {
      return;
    }

    control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: boolean) => {
        // OFF (false) になったときにリセット
        if (val === false) {
          resetSensorSettings(
            this.formGroup,
            this.assettypeRestoreValue,
            sensor,
          );
        }
      });
  }
}
